import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Select, SegmentedToggle, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import projectEnvironmentApi, { ProjectEnvironment } from '../../services/projectEnvironment';
import { slugify } from '../../utils/slug';
import { sanitizeChange } from '../../utils/sanitizeInput';
import {
  CUSTOM_DOMAIN_MAX_LENGTH,
  ENVIRONMENT_NAME_MAX_LENGTH,
  ENVIRONMENT_NAME_PATTERN,
  displayEnvironmentName,
  extractErrorDetail,
} from './types';

const BASE_DOMAIN = 'veyloapp.ai';
const DEBOUNCE_MS = 450;

/** One field's verdict. `idle` covers empty, still-typing and not-yet-checked. */
interface FieldStatus {
  kind: 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error';
  msg?: string;
}

/** Format verdict. `soft` errors stay quiet until the field is blurred —
 *  a trailing hyphen is usually a keystroke away from being valid. */
interface FormatResult {
  ok: boolean;
  idle?: boolean;
  soft?: boolean;
  msg?: string;
}

const validateName = (value: string): FormatResult => {
  // Soft, so an untouched empty field shows the neutral hint, not an error —
  // the form must not open shouting "required" before the user typed anything.
  if (!value) return { ok: false, msg: 'Environment name is required.', soft: true };
  if (!/^[a-z0-9]/.test(value))
    return { ok: false, msg: 'Start with a letter or number.', soft: value.length < 2 };
  if (value.endsWith('-')) return { ok: false, msg: "Can't end with a hyphen.", soft: true };
  if (!ENVIRONMENT_NAME_PATTERN.test(value))
    return { ok: false, msg: 'Lowercase letters, numbers and hyphens only.' };
  return { ok: true };
};

const validateDomain = (value: string): FormatResult => {
  // Soft for the same reason as the name: no shouting before the user typed.
  if (!value) return { ok: false, msg: 'Custom domain is required.', soft: true };
  if (!/^[a-z0-9]/.test(value))
    return { ok: false, msg: 'Start with a letter or number.', soft: true };
  if (value.endsWith('-')) return { ok: false, msg: "Can't end with a hyphen.", soft: true };
  if (value.length > CUSTOM_DOMAIN_MAX_LENGTH)
    return {
      ok: false,
      msg: `Max ${CUSTOM_DOMAIN_MAX_LENGTH} characters — use a shorter name or edit the domain.`,
    };
  return { ok: true };
};

const statusIcon = (status: FieldStatus): React.ReactNode => {
  if (status.kind === 'checking')
    return <Icon name={IconName.Loading} size="sm" spin className="text-text-tertiary" inheritColor />;
  if (status.kind === 'available') return <Icon name={IconName.Check} size="sm" variant="success" />;
  if (status.kind === 'taken' || status.kind === 'invalid' || status.kind === 'error')
    return <Icon name={IconName.Close} size="sm" variant="error" />;
  return undefined;
};

const toneOf = (status: FieldStatus): 'good' | 'bad' | '' =>
  status.kind === 'available'
    ? 'good'
    : status.kind === 'taken' || status.kind === 'invalid' || status.kind === 'error'
    ? 'bad'
    : '';

const inputVariantOf = (status: FieldStatus): 'success' | 'error' | 'default' =>
  toneOf(status) === 'good' ? 'success' : toneOf(status) === 'bad' ? 'error' : 'default';

/** Debounced availability of one field (used for both the name and the
 *  domain). `enabled` gates the call entirely; a sequence counter drops
 *  responses that no longer describe the current value. */
function useAvailability(
  value: string,
  enabled: boolean,
  check: (value: string) => Promise<{ available: boolean; reason?: string | null }>,
  takenFallback: string
): FieldStatus {
  const [status, setStatus] = useState<FieldStatus>({ kind: 'idle' });
  const seq = useRef(0);

  useEffect(() => {
    seq.current++;
    if (!enabled) {
      setStatus({ kind: 'idle' });
      return;
    }
    setStatus({ kind: 'checking' });
    const id = seq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await check(value);
        if (id !== seq.current) return;
        setStatus(
          result.available
            ? { kind: 'available' }
            : { kind: 'taken', msg: result.reason || takenFallback }
        );
      } catch {
        if (id !== seq.current) return;
        setStatus({ kind: 'error', msg: "Couldn't reach the server. It'll retry as you type." });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled]);

  return status;
}

/** Where a new environment's code comes from. Fixed at creation — a main is fed
 *  by the project, a sub only ever runs what its main runs. Not offered on edit,
 *  because changing it would change what the environment *is*. */
type Source = 'current' | 'environment';

interface EnvironmentFormViewProps {
  projectId: string;
  /** What the backend prefixes default URLs with — the project's custom domain,
   *  or the project id when it has none. */
  domainPrefix: string;
  /** Present = edit an existing environment; absent = create a new one. */
  environment?: ProjectEnvironment;
  /** The project's main environments, offered as a source when creating.
   *  A main does not need to be deployed to be chosen — creating a sub only
   *  reserves a name, a URL and ports. */
  /** Every environment a new one may be added after: the end of each chain. */
  parents: ProjectEnvironment[];
  /** Receives the saved environment; `created` is true only for a brand-new
   *  environment, so the panel chains a deploy onto creates but not renames. */
  onSaved: (saved: ProjectEnvironment, created: boolean) => Promise<void>;
  onCancel: () => void;
}

/** Create / edit form: a required name, and a custom domain that follows the
 *  name (`<domainPrefix>-<name>`) until the user takes it over — with a
 *  "Match the name again" escape hatch back.
 *
 *  Each field checks its own availability as the user types, debounced —
 *  /check-name for the name (unique per project), /check-domain for the URL
 *  (unique across projects and environments) — and shows its state inside the
 *  input (spinner / check / cross) with one message line below. Format errors
 *  that just mean "still typing" stay quiet until the field is blurred. */
export const EnvironmentFormView: React.FC<EnvironmentFormViewProps> = ({
  projectId,
  domainPrefix,
  environment,
  parents,
  onSaved,
  onCancel,
}) => {
  const isEdit = !!environment;
  const initialName = environment?.environment_name || '';
  const initialDomain = environment?.custom_domain || '';

  const [name, setName] = useState(initialName);
  // A new form opens with the main app's domain already in the field; the
  // environment name extends it as it is typed.
  const [domain, setDomain] = useState(isEdit ? initialDomain : domainPrefix);
  // While linked, the domain mirrors the name. Editing the domain unlinks it;
  // a stored domain on edit is already the user's, so it starts unlinked.
  const [linked, setLinked] = useState(!isEdit);
  const [nameTouched, setNameTouched] = useState(false);
  const [domainTouched, setDomainTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [source, setSource] = useState<Source>('current');
  const [parentId, setParentId] = useState<string>(parents[0]?.id || '');

  const nameFormat = useMemo(() => validateName(name), [name]);
  const domainFormat = useMemo(() => validateDomain(domain), [domain]);

  const unchanged = isEdit && name === initialName && domain === initialDomain;

  // Each field asks its own question. The environment's own stored values are
  // not candidates — don't re-ask about them.
  const nameAvail = useAvailability(
    name,
    nameFormat.ok && !(isEdit && name === initialName),
    (value) => projectEnvironmentApi.checkName(projectId, value, environment?.id),
    'This name is not available.'
  );
  // While linked with no name yet, the field holds the bare prefix — the main
  // app's own domain — which is a starting value, not a candidate.
  const domainAvail = useAvailability(
    domain,
    domainFormat.ok && !(isEdit && domain === initialDomain) && !(linked && !name),
    (value) => projectEnvironmentApi.checkDomain(projectId, value, environment?.id),
    'This URL is already taken.'
  );

  // Format errors overrule availability; soft ones wait for blur.
  const resolve = (format: FormatResult, avail: FieldStatus, touched: boolean): FieldStatus => {
    if (!format.ok) {
      if (format.idle || (format.soft && !touched)) return { kind: 'idle' };
      return { kind: 'invalid', msg: format.msg };
    }
    return avail;
  };
  const nameStatus = resolve(nameFormat, nameAvail, nameTouched);
  const domainStatus = resolve(domainFormat, domainAvail, domainTouched);

  // Same rule and same caret handling as the project's custom domain field —
  // the two used to disagree about what typing a space did.
  const cleanName = (v: string) => slugify(v, ENVIRONMENT_NAME_MAX_LENGTH);
  const cleanDomain = (v: string) => slugify(v, CUSTOM_DOMAIN_MAX_LENGTH);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeChange(e, cleanName);
    setName(next);
    setFormError(null);
    if (linked) setDomain(next ? `${domainPrefix}-${next}` : domainPrefix);
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinked(false);
    setDomain(sanitizeChange(e, cleanDomain));
    setFormError(null);
  };

  const relinkDomain = () => {
    setLinked(true);
    setDomainTouched(false);
    setDomain(name ? `${domainPrefix}-${name}` : domainPrefix);
  };

  const handleSave = async () => {
    try {
      setFormError(null);
      setSaving(true);
      const saved =
        isEdit && environment
          ? await projectEnvironmentApi.updateEnvironment(projectId, environment.id, name, domain)
          : await projectEnvironmentApi.createEnvironment(
              projectId,
              name,
              domain || undefined,
              source === 'environment' ? parentId : undefined
            );
      await onSaved(saved, !isEdit);
    } catch (e: any) {
      setFormError(
        extractErrorDetail(e, isEdit ? 'Failed to update environment' : 'Failed to create environment')
      );
      setSaving(false);
    }
  };

  // An unchanged own value passes without a check — it is already this
  // environment's name or URL.
  const nameOk = nameStatus.kind === 'available' || (isEdit && name === initialName);
  const domainOk = domainStatus.kind === 'available' || (isEdit && domain === initialDomain);
  const ready = nameOk && domainOk && !saving && !unchanged;

  const nameMessage =
    nameStatus.msg ||
    (nameStatus.kind === 'checking'
      ? 'Checking availability…'
      : nameStatus.kind === 'available'
      ? 'Name is available.'
      : 'Lowercase letters, numbers and hyphens.');

  const domainMessage =
    domainStatus.msg ||
    (domainStatus.kind === 'checking'
      ? 'Checking availability…'
      : domainStatus.kind === 'available'
      ? 'Domain is available.'
      : 'Edit it if you want something other than the suggestion.');

  const messageColor = (status: FieldStatus): 'error' | 'success' | 'tertiary' =>
    toneOf(status) === 'bad' ? 'error' : toneOf(status) === 'good' ? 'success' : 'tertiary';

  return (
    <div className="flex flex-col gap-bg-v2-xl w-full">
      <div>
        <Input
          id="environment-name"
          name="environment-name"
          label="Environment Name"
          value={name}
          placeholder={"Enter environment name"}
          onChange={handleNameChange}
          onBlur={() => setNameTouched(true)}
          maxLength={ENVIRONMENT_NAME_MAX_LENGTH}
          autoComplete="off"
          spellCheck={false}
          variant={inputVariantOf(nameStatus)}
          endIcon={statusIcon(nameStatus)}
          autoFocus
        />
        <Text variant="text-xs" color={messageColor(nameStatus)} className="mt-bg-v2-xs break-words block">
          {nameMessage}
        </Text>
      </div>

      <div>
        <div className="mb-bg-v2-xs flex items-baseline justify-between gap-bg-v2-md">
          <Text variant="text-sm" weight="medium" color="secondary">
            Custom Domain
          </Text>
          {linked ? (
            <Text variant="text-xs" color="tertiary">
              Follows the name
            </Text>
          ) : (
            <button
              type="button"
              onClick={relinkDomain}
              disabled={!name}
              className="text-bg-v2-xs font-bg-v2-medium text-text-tertiary underline underline-offset-2 hover:text-text-primary transition-colors disabled:cursor-not-allowed"
            >
              Match the name again
            </button>
          )}
        </div>
        <Input
          id="environment-custom-domain"
          name="environment-custom-domain"
          value={domain}
          onChange={handleDomainChange}
          onBlur={() => setDomainTouched(true)}
          maxLength={CUSTOM_DOMAIN_MAX_LENGTH}
          autoComplete="off"
          spellCheck={false}
          variant={inputVariantOf(domainStatus)}
          endIcon={statusIcon(domainStatus)}
          suffix={`.${BASE_DOMAIN}`}
        />
        <Text variant="text-xs" color={messageColor(domainStatus)} className="mt-bg-v2-xs break-words block">
          {domainMessage}
        </Text>
      </div>

      {isEdit && (
        <Text variant="text-xs" color="tertiary">
          Renaming does not change the URL. Changing the domain moves the
          environment immediately — links to the old address stop working, and
          there is no redirect.
        </Text>
      )}

      {/* Source — creation only. What feeds an environment is what it *is*, and
          it is fixed once made: a main takes the project's code, a sub only ever
          runs what its main runs. */}
      {!isEdit && (
        <div className="flex flex-col gap-bg-v2-sm">
          <Text variant="text-xs" weight="medium" color="secondary">
            Source
          </Text>
          <SegmentedToggle<Source>
            ariaLabel="Environment source"
            fullWidth
            value={source}
            onChange={setSource}
            disabled={saving}
            options={[
              { value: 'current', label: 'Current app', icon: IconName.Code },
              {
                value: 'environment',
                label: 'Environment',
                icon: IconName.Environment,
                // Nothing to pick from until a chain has an end to append to.
                // Shown and explained rather than hidden — it is half of what
                // the control is describing.
                disabled: parents.length === 0,
                title:
                  parents.length === 0
                    ? 'Create an environment from the Current app first'
                    : undefined,
              },
            ]}
          />

          {source === 'current' ? (
            <Text variant="text-xs" color="tertiary">
              {parents.length === 0
                ? 'Deploys take the code currently in the Current app. Create this first — everything else is added after it.'
                : 'Deploys take the code currently in the Current app.'}
            </Text>
          ) : (
            <Select
              value={parentId}
              disabled={saving}
              onChange={(value) => setParentId(String(value))}
              // Only the end of a chain: a chain grows at its end, so anything
              // that already has an environment after it is not offered.
              options={parents.map((p) => ({
                label: displayEnvironmentName(p.environment_name),
                value: p.id,
              }))}
              helperText="This environment is added after the one selected, and promotes from it. Deploys take the code running there."
            />
          )}
        </div>
      )}

      {formError && (
        <div className="flex items-center gap-bg-v2-xs p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0" inheritColor />
          <Text variant="text-xs" color="error">
            {formError}
          </Text>
        </div>
      )}

      <div className="flex justify-end gap-bg-v2-md pt-bg-v2-md border-t border-border-secondary">
        <Button variant="outline" label="Cancel" type="button" onClick={onCancel} disabled={saving} size={"small"} />
        <Button
          variant="primary"
          type="button"
          onClick={handleSave}
          size={"small"}
          disabled={!ready}
          label={
            saving ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save Changes' : 'Create Environment'
          }
        />
      </div>
    </div>
  );
};

export default EnvironmentFormView;
